import { useBillingStore } from '@/stores/billing'
import { useCommonStore } from '@/stores/common'
import { usePaywallStore } from '@/stores/paywall'
import { useProfileStore } from '@/stores/profile'
import { useUsageStore, type UsageResource } from '@/stores/usage'
import { trackLimitReached } from '@/services/metrikaGoals'
import { CLIENT_LIMIT_GATE, SUSPENDED_GATE_ENABLED } from '@/constants/pro'
import type { TopicObject } from '@/types'

/**
 * Решение гейта. Намеренно возвращаем решение, а не выполняем действие:
 * так одну и ту же логику можно переиспользовать в TagList/ThreadView/HeaderComponent
 * и покрыть юнит-тестом без монтирования компонентов.
 */
export type GateDecision = 'allow' | 'login' | 'paywall' | 'pending' | 'suspended'

/**
 * Решение по остатку ресурса. Отдельно от GateDecision: там про доступ, здесь про квоту.
 * Исключение — 'suspended': оно про доступ, но живёт в обоих перечислениях, потому что
 * checkResource — единственный гейт перед отправкой текста, изображения и песни.
 */
export type ResourceDecision = 'allow' | 'limit' | 'pending' | 'suspended'

/**
 * Что показать в шторке, если решение — 'paywall'. Передаётся отдельно от решения:
 * сам GateDecision остаётся строкой, которую сравнивают вызывающий код и тесты.
 */
export type PaywallContext = { kind: 'topic'; topicName: string } | { kind: 'limit'; resource: UsageResource }

/** Отсутствие access = 'free': бэк может выкатиться позже фронта. */
export function isProTopic(topic: Pick<TopicObject, 'access'>): boolean {
	return topic.access === 'pro'
}

/** Непокрытая ветка решения — ошибка типов, а не молчаливый фолбэк на пейволл. */
function assertNever(decision: never): never {
	throw new Error(`неизвестное решение гейта: ${String(decision)}`)
}

export function useFeatureGate() {
	const usageStore = useUsageStore()
	const commonStore = useCommonStore()
	const profileStore = useProfileStore()
	const paywallStore = usePaywallStore()
	const billingStore = useBillingStore()

	function isAwaitingPayment(): boolean {
		return billingStore.isAwaitingPayment
	}

	/**
	 * Несостоявшееся продление. Проверяется после isAwaitingPayment и раньше всего
	 * остального: человеку, у которого списание уже в процессе, «не удалось продлить»
	 * говорить нельзя — деньги в игре, правдивее шторка ожидания оплаты.
	 */
	function isSuspended(): boolean {
		return SUSPENDED_GATE_ENABLED && usageStore.isSuspended
	}

	/**
	 * Тариф: авторитетный источник — /usage, /user держим как быстрый дубль (§5.2).
	 * Пустое значение = ни срез, ни профиль не пришли → не блокируем, подстрахует 402.
	 */
	function isFreePlan(): boolean {
		return (usageStore.plan || profileStore.plan) === 'free'
	}

	/**
	 * Гейт проверяет только доступ к платной функции. Остаток запросов — отдельно,
	 * в checkResource: доступ и квота исчерпываются по разным причинам и показывают
	 * разные шторки.
	 */
	function checkTopic(topic: TopicObject): GateDecision {
		if (isAwaitingPayment()) return 'pending'
		// До проверки isProTopic, а не внутри неё: при сорванном продлении закрыты все
		// генерации, поэтому клик даже по бесплатной теме не должен доходить до createThread.
		if (isSuspended()) return 'suspended'
		if (isProTopic(topic)) {
			if (usageStore.isGuest) return 'login'
			// Неизвестный plan (профиль не отдался) — пропускаем: подстрахует 402 от сервера.
			return isFreePlan() ? 'paywall' : 'allow'
		}
		return 'allow'
	}

	/**
	 * Остаток ресурса перед отправкой. Пускаем, если:
	 * - перед нами гость — его квоту считает сервер (403 GUEST_LIMIT_EXCEEDED → модалка входа);
	 * - гейт выключен (CLIENT_LIMIT_GATE) — тогда всё решает 402 от сервера, как раньше;
	 * - среза нет (сетевой сбой, холодный старт) — fail-open, запирать платящего нельзя.
	 * Иначе смотрим remaining: сервер остаётся предохранителем в любом случае.
	 */
	function checkResource(resource: UsageResource): ResourceDecision {
		if (isAwaitingPayment()) return 'pending'
		if (isSuspended()) return 'suspended'
		// Гостя клиентский гейт не считает: в его срезе image/song занулены самим
		// normalizeUsage (бэк их гостю не считает), и по ним гейт запирал бы выдуманный лимит.
		if (usageStore.isGuest) return 'allow'
		if (!CLIENT_LIMIT_GATE) return 'allow'
		const limits = usageStore.limits
		if (!limits) return 'allow'
		return limits[resource].remaining === 0 ? 'limit' : 'allow'
	}

	/**
	 * Красить ли плитку темы как платную. Pro-юзеру платных тем не существует —
	 * градиент у него читался бы как замок на открытой двери.
	 */
	function shouldMarkPro(topic: Pick<TopicObject, 'access'>): boolean {
		return isProTopic(topic) && !usageStore.isPro
	}

	/**
	 * Выполняет решение. true — действие можно продолжать.
	 * context нужен только для 'paywall' — он определяет заголовок шторки.
	 */
	function runGate(decision: GateDecision, context?: PaywallContext): boolean {
		switch (decision) {
			case 'allow':
				return true
			case 'pending':
				billingStore.showWaiting()
				return false
			case 'suspended':
				paywallStore.openSuspended()
				return false
			case 'login':
				commonStore.openLoginModal()
				return false
			case 'paywall':
				if (context?.kind === 'topic') {
					paywallStore.openForTopic(context.topicName)
				} else {
					paywallStore.openForLimit(context?.resource ?? 'text')
				}
				return false
			default:
				// Новое решение добавили в тип, но не сюда: пусть падает на сборке типов.
				// Прежний if-каскад в этом случае молча показывал продажу Pro.
				return assertNever(decision)
		}
	}

	/**
	 * Выполняет решение по остатку. Отдельно от runGate: там про доступ к функции,
	 * здесь про квоту, и шторки у них разные.
	 */
	function runResourceGate(decision: ResourceDecision, resource: UsageResource): boolean {
		switch (decision) {
			case 'allow':
				return true
			case 'pending':
				billingStore.showWaiting()
				return false
			case 'suspended':
				paywallStore.openSuspended()
				return false
			case 'limit':
				paywallStore.openLimitDialog(resource)
				trackLimitReached('plan', resource)
				return false
			default:
				return assertNever(decision)
		}
	}

	return { checkTopic, checkResource, shouldMarkPro, runGate, runResourceGate }
}
